// =============================================================================
// SINGLE TENANT CONFIG FILE
// =============================================================================
// All multi-tenant behaviour is defined here: theme, copy overrides, assets,
// meta, and default tenant. No other file defines tenant data.
// To add a new tenant, add an entry to the `tenants` record below.
// =============================================================================

/**
 * Deep-partial helper: makes every nested property optional.
 */
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

/**
 * Shape of the base i18n translation files (en.json / pt.json).
 * Only the keys you want to override per tenant need to be present.
 */
type TranslationOverrides = DeepPartial<Record<string, unknown>>;

/**
 * Full configuration for a single tenant.
 */
export type TenantConfig = {
  /** CSS variable overrides injected on :root. Only include variables you want to change. */
  theme: Record<string, string>;

  /** Optional i18n copy overrides per language. Only overridden keys need to be present. */
  copy?: {
    en?: TranslationOverrides;
    pt?: TranslationOverrides;
    fr?: TranslationOverrides;
    es?: TranslationOverrides;
    it?: TranslationOverrides;
  };

  /** Paths to tenant-specific assets (relative to public/) */
  assets: {
    heroImageUrl: string;
    faviconUrl: string;
  };

  /** Document-level metadata */
  meta: {
    documentTitle: string;
    metaDescription?: string;
    /** Organization name used in the email subject and footer message (e.g. "Devoteam", "KLx"). */
    corporateOrganization?: string;
  };

  /** Optional Google Fonts (or other) URL to load a different font family */
  fontUrl?: string;
};

// -----------------------------------------------------------------------------
// Default tenant id
// Used when hostname is viagify.com, localhost, or an unknown subdomain.
// -----------------------------------------------------------------------------
export const defaultTenantId = "solace";

// -----------------------------------------------------------------------------
// Tenant registry
// Each key is the subdomain (implicit mapping: subdomain = tenant id).
// -----------------------------------------------------------------------------
export const tenants: Record<string, TenantConfig> = {
  // ---------------------------------------------------------------------------
  // DEVOTEAM — current / default look & feel
  // ---------------------------------------------------------------------------
  devoteam: {
    theme: {
      // Brand / primary
      "--brand-primary": "#2563eb",
      "--brand-primary-dark": "#1e40af",
      "--brand-secondary": "#10b981",
      "--brand-accent": "#f59e0b",

      // Heading accent
      "--heading-color": "#F8485E",

      // App gradient
      "--app-gradient-start": "#0E00FF",
      "--app-gradient-mid": "#CBCAFE",
      "--app-gradient-end": "#F8485E",

      // Font
      "--font-family": '"Montserrat", sans-serif',
    },
    copy: {
      // No overrides needed — base en.json / pt.json are the defaults
    },
    assets: {
      heroImageUrl: "/tenants/devoteam/hero.svg",
      faviconUrl: "/favicon.svg",
    },
    meta: {
      documentTitle: "Viagify for Devoteam",
      metaDescription:
        "Request a personalized travel quote for your dream trip",
      corporateOrganization: "Devoteam",
    },
    fontUrl:
      "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap",
  },

  // ---------------------------------------------------------------------------
  // KLx — placeholder second tenant (replace values with real branding)
  // ---------------------------------------------------------------------------
  klx: {
    theme: {
      // Brand / primary — example: teal palette
      "--brand-primary": "#00a8e3",
      "--brand-primary-dark": "#007399",
      "--brand-secondary": "#10b981",
      "--brand-accent": "#f59e0b",

      // Heading accent
      "--heading-color": "#00a8e3",

      // App gradient
      "--app-gradient-start": "#0E00FF",
      "--app-gradient-mid": "#CBCAFE",
      "--app-gradient-end": "#00a8e3",

      // Font
      "--font-family": '"Poppins", sans-serif',
    },
    copy: {
      // en: {
      //   welcome: {
      //     title: "Travel Planner",
      //     description:
      //       "Tell us about your ideal trip and we'll craft the perfect proposal for you!",
      //   },
      // },
      // pt: {
      //   welcome: {
      //     title: "Planeador de Viagens",
      //     description:
      //       "Conte-nos sobre a sua viagem ideal e criaremos a proposta perfeita para si!",
      //   },
      // },
    },
    assets: {
      heroImageUrl: "/tenants/klx/hero.svg",
      faviconUrl: "/tenants/klx/favicon.svg",
    },
    meta: {
      documentTitle: "Viagify for KLx",
      metaDescription:
        "Request a personalized travel quote for your dream trip",
      corporateOrganization: "KLx",
    },
    fontUrl:
      "https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap",
  },
  // ---------------------------------------------------------------------------
  // Itron (Urbint)  — placeholder second tenant (replace values with real branding)
  // ---------------------------------------------------------------------------
  itron: {
    theme: {
      // Brand / primary — example: teal palette
      "--brand-primary": "#CB2C30",
      "--brand-primary-dark": "#651619",
      "--brand-secondary": "#183247",
      "--brand-accent": "#f59e0b",

      // Heading accent
      "--heading-color": "#CB2C30",

      // App gradient
      "--app-gradient-start": "#0E00FF",
      "--app-gradient-mid": "#CBCAFE",
      "--app-gradient-end": "#CB2C30",

      // Font
      "--font-family": '"Source Sans 3", sans-serif',
    },
    copy: {
      // en: {
      //   welcome: {
      //     title: "Travel Planner",
      //     description:
      //       "Tell us about your ideal trip and we'll craft the perfect proposal for you!",
      //   },
      // },
      // pt: {
      //   welcome: {
      //     title: "Planeador de Viagens",
      //     description:
      //       "Conte-nos sobre a sua viagem ideal e criaremos a proposta perfeita para si!",
      //   },
      // },
    },
    assets: {
      heroImageUrl: "/tenants/itron/hero.svg",
      faviconUrl: "/tenants/itron/favicon.svg",
    },
    meta: {
      documentTitle: "Viagify for Itron",
      metaDescription:
        "Request a personalized travel quote for your dream trip",
      corporateOrganization: "Itron",
    },
    fontUrl:
      "https://fonts.googleapis.com/css2?family=Source+Sans+3:ital,wght@0,200..900;1,200..900&display=swap",
  },
    // ---------------------------------------------------------------------------
  // NIW  — placeholder second tenant (replace values with real branding)
  // ---------------------------------------------------------------------------
  niw: {
    theme: {
      // Brand / primary — example: teal palette
      "--brand-primary": "#1B1464",
      "--brand-primary-dark": "#0D055C",
      "--brand-secondary": "#D4DB21",
      "--brand-accent": "#f59e0b",

      // Heading accent
      "--heading-color": "#1B1464",

      // App gradient
      "--app-gradient-start": "#0E00FF",
      "--app-gradient-mid": "#D4DB21",
      "--app-gradient-end": "#1B1464",

      // Font
      "--font-family": '"Sora", sans-serif',
    },
    copy: {
      // en: {
      //   welcome: {
      //     title: "Travel Planner",
      //     description:
      //       "Tell us about your ideal trip and we'll craft the perfect proposal for you!",
      //   },
      // },
      // pt: {
      //   welcome: {
      //     title: "Planeador de Viagens",
      //     description:
      //       "Conte-nos sobre a sua viagem ideal e criaremos a proposta perfeita para si!",
      //   },
      // },
    },
    assets: {
      heroImageUrl: "/tenants/niw/hero.svg",
      faviconUrl: "/tenants/niw/favicon.svg",
    },
    meta: {
      documentTitle: "Viagify for NiW",
      metaDescription:
        "Request a personalized travel quote for your dream trip",
      corporateOrganization: "NiW",
    },
    fontUrl:
      "https://fonts.googleapis.com/css2?family=Sora:ital,wght@0,100..800;1,100..800&display=swap",
  },
    // ---------------------------------------------------------------------------
  // NIW  — placeholder second tenant (replace values with real branding)
  // ---------------------------------------------------------------------------
  solace: {
    theme: {
      // Brand / primary — example: teal palette
      "--brand-primary": "#5AC499",
      "--brand-primary-dark": "#1F5640",
      "--brand-secondary": "#E8FFE5",
      "--brand-accent": "#f59e0b",

      // Heading accent
      "--heading-color": "#5AC499",

      // App gradient
      "--app-gradient-start": "#0E00FF",
      "--app-gradient-mid": "#E8FFE5",
      "--app-gradient-end": "#5AC499",

      // Font
      "--font-family": '"Figtree", sans-serif',
    },
    copy: {
      // en: {
      //   welcome: {
      //     title: "Travel Planner",
      //     description:
      //       "Tell us about your ideal trip and we'll craft the perfect proposal for you!",
      //   },
      // },
      // pt: {
      //   welcome: {
      //     title: "Planeador de Viagens",
      //     description:
      //       "Conte-nos sobre a sua viagem ideal e criaremos a proposta perfeita para si!",
      //   },
      // },
    },
    assets: {
      heroImageUrl: "/tenants/solace/hero.svg",
      faviconUrl: "/tenants/solace/favicon.svg",
    },
    meta: {
      documentTitle: "Viagify for Solace",
      metaDescription:
        "Request a personalized travel quote for your dream trip",
      corporateOrganization: "Solace",
    },
    fontUrl:
      "https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap",
  }
};
